import React, { memo, useState } from "react";
import { Grid, Box, Button } from "@mui/material";
import { Formik, Form, FormikProps } from "formik";
import { view, lensPath } from "ramda";
import { User, Data } from "../../../store/user/types";
import { useDispatch, useSelector } from "react-redux";
import { ApplicationState } from "../../../store";
import { createSchema, editSchema } from "./userEditForm.schema";
import { useHistory } from "react-router-dom";
import { getById } from "../../../store/user/queries";
import { saveRequest } from "../../../store/user/actions";
import pageStyles from "../../pageStyles";
import Confirm from "../../../common/confirm/Confirm";
import FormSubmitButtons from "../../../common/buttons/FormSubmitButtons";
import { Data as SessionData } from "../../../store/session/types";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import TextInput from "../../../common/formElements/TextInput";
import SelectDropDown, {
  Option,
} from "../../../common/formElements/SelectDropDown";
import { saveRequest as ResetMfa } from "../../../store/resetMfa/actions";
import { useToastMessage } from "../../../effects/useToastMessage";
import { toast } from "react-toastify";

interface FormValues {
  id?: string;
  Email?: string;
  Enabled?: boolean;
  FirstName?: string;
  LastName?: string;
  Company?: string;
  Department?: string;
  Phone?: string;
  Group?: string;
  changePassword?: boolean;
}

interface UserEditForm {
  mode: string;
  id: string;
  session?: SessionData | null;
  isCreate: boolean;
}

interface MapToUserDataProps {
  values: FormValues;
  session?: SessionData | null;
  changePassword?: boolean;
}

const mapToForm = (user: Data | undefined | null): FormValues => {
  return {
    id: user?.id,
    Email: (user && view(lensPath(["attributes", "Email"]), user)) || "",
    Enabled: (user && view(lensPath(["attributes", "Enabled"]), user)) || false,
    FirstName:
      (user && view(lensPath(["attributes", "FirstName"]), user)) || "",
    LastName: (user && view(lensPath(["attributes", "LastName"]), user)) || "",
    Company: (user && view(lensPath(["attributes", "Company"]), user)) || "",
    Department:
      (user && view(lensPath(["attributes", "Department"]), user)) || "",
    Phone: (user && view(lensPath(["attributes", "Phone"]), user)) || "",
    Group: (user && view(lensPath(["attributes", "Group"]), user)) || "",
  };
};

const mapToUserData = ({
  values,
  session,
  changePassword,
}: MapToUserDataProps): Data => {
  const data = {
    id: values.id || undefined,
    type: "User",
    attributes: {
      Email: values.Email || "",
      Enabled: true,
      FirstName: values.FirstName || "",
      Group: values.Group || "",
      LastName: values.LastName,
      Company: values.Company,
      Department: values.Department,
      Phone: values.Phone,
    },
    changePassword: changePassword,
  };
  return data;
};

const UserEditForm = (props: UserEditForm) => {
  const { mode, id, session, isCreate } = props;

  const dispatch = useDispatch();
  const history = useHistory();
  const classes = pageStyles();

  const [showConfirm, setShowConfirm] = useState(false);
  const [showResetMfa, setShowResetMfa] = useState(false);

  const userGroup = useSelector(
    (state: ApplicationState) => state.userGroup.data
  );

  const resetMfa: any = useSelector(
    (state: ApplicationState) => state.resetMfa.data
  );

  const callBackReset = (err: string | null): void => {
    if (err) {
      const e: {
        detail: string | undefined;
        title: string | undefined;
      } = JSON.parse(err);
      let errText = "";
      errText += `Reset failed! An error occurred while resetting your MFA.`;
      toast.error(errText, {
        closeOnClick: true,
      });
    } else {
      toast.success("Reset succeeded! Your MFA has been successfully reset.", {
        closeOnClick: true,
      });
    }
  };

  const ResetMfaFunction = (user_id: string) => {
    const resetMfaData = {
      user_id: user_id,
    };
    dispatch(ResetMfa(resetMfaData, (err) => callBackReset(err)));
    setShowResetMfa(false);
  };

  const userGroupOptions: Option[] =
    (userGroup &&
      userGroup?.data &&
      userGroup?.data.map((group) => group.attributes.Name)) ||
    [];

  const user = useSelector(id ? getById(id) : () => null) as Data;
  const initialValues = mapToForm(user);
  const validationSchema = id ? editSchema : createSchema;
  const showPasswordFields = mode === "CREATE" || session?.id === id;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, actions) => {
        const userData = mapToUserData({ values, session });
        const callback = (err: string | null, data: User | null) => {
          if (err) {
            console.log(err);
            let errText = `An error occurred while saving: ${err}`;
            toast.error(errText, {
              closeOnClick: true,
            });
          } else {
            // Show success toast for both create and update
            const successMessage = isCreate ? "User created successfully!" : "User updated successfully!";
            toast.success(successMessage, {
              closeOnClick: true,
            });
            
            // Navigate back for creation after showing the toast
            if (isCreate) {
              setTimeout(() => {
                history.goBack();
              }, 1500); // Wait 1.5 seconds for user to see the success message
            }
          }
        };

        dispatch(saveRequest(userData, callback));
      }}
    >
      {(props: FormikProps<any>) => {
        const { values, errors, isSubmitting } = props;

        // Show toast errors
        useToastMessage(isSubmitting, errors);

        const handleChangePasswordClick = () => {
          const userData = mapToUserData({ values, changePassword: true });
          const callback = (err: string | null, data: User | null) => {
            if (err) {
              console.log(err);
              let errText = `An error occurred while saving: ${err}`;
              toast.error(errText, {
                closeOnClick: true,
              });
            } else {
              // Show success toast for both create and update
              const successMessage = isCreate ? "User created successfully!" : "User updated successfully!";
              toast.success(successMessage, {
                closeOnClick: true,
              });
              
              // Navigate back for creation after showing the toast
              if (isCreate) {
                setTimeout(() => {
                  history.goBack();
                }, 1500); // Wait 1.5 seconds for user to see the success message
              }
            }
          };
          dispatch(saveRequest(userData, callback));
          setShowConfirm(false);
        };

        console.log("usergroup options" + userGroupOptions);
        return (
          <Form>
            <Grid
              container
              alignItems="flex-start"
              spacing={2}
              className={classes.formWrapper as string}
            >
              <Grid item xs={12} md={4}>
                <TextInput fieldname="FirstName" label="First Name*" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextInput fieldname="LastName" label="Last Name" />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextInput
                  fieldname="Email"
                  label="Email*"
                  disabled={mode === "UPDATE"}
                />
              </Grid>

              <Grid item xs={12} md={4}>
                {userGroupOptions && userGroupOptions.length > 0 && session.id !== id ? (
                  <SelectDropDown
                    fieldname="Group"
                    label="Role"
                    options={userGroupOptions}
                  />
                ) : (
                  <TextInput fieldname="Group" label="Role" disabled />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <TextInput fieldname="Department" label="Department" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextInput
                  fieldname="Phone"
                  label="Phone"
                  inputProps={{ maxLength: 15 }}
                />
              </Grid>
              {!showPasswordFields && (
                <Grid item xs={12}>
                  <hr className={classes.devide} />
                  <Box display="flex" justifyContent="flex-end">
                    <Box flex={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        startIcon={<VpnKeyIcon />}
                        onClick={() => setShowConfirm(true)}
                      >
                        Change Password
                      </Button>
                    </Box>

                    <Confirm
                      isOpen={showConfirm}
                      onCancel={() => setShowConfirm(false)}
                      onConfirm={() => handleChangePasswordClick()}
                      title="Change Password"
                      icon="danger"
                      confirmLabel="Yes"
                      contentText={`You are about to Change a user's password. Are you sure, you want to do this?`}
                    />

                    <Box flex={1}>
                      <Button
                        variant="outlined"
                        color="primary"
                        size="medium"
                        startIcon={<VpnKeyIcon />}
                        onClick={() => setShowResetMfa(true)}
                      >
                        RESET MFA
                      </Button>
                    </Box>

                    <Confirm
                      isOpen={showResetMfa}
                      onCancel={() => setShowResetMfa(false)}
                      onConfirm={() => ResetMfaFunction(user.id)}
                      title="Reset MFA "
                      icon="danger"
                      confirmLabel="Yes"
                      contentText={`You are about to reset a user's MFA. Are you sure, you want to do this?`}
                    />
                  </Box>
                </Grid>
              )}
              <FormSubmitButtons isDisabled={isSubmitting} />
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default UserEditForm;
